/**
 * Sigfox link-quality (RSSI) shapes, mirroring
 * atcreate-express-api/src/services/signalStrength.service.ts.
 *
 * Background: the fleet's missing-message gaps were root-caused to marginal RF,
 * not software. Margin is measured against the Sigfox demodulation floor of
 * about -142 dBm; a device a few dB above it loses most of its transmissions
 * whenever conditions shift.
 */

/** Nominal Sigfox base station sensitivity, in dBm. Keep in step with the API. */
export const SENSITIVITY_DBM = -142

/**
 * Longest window the signal endpoint serves (SIGNAL_MAX_DAYS in the API); it
 * clamps anything longer, and samples are only retained this long anyway.
 */
export const SIGNAL_MAX_DAYS = 90

/**
 * Coarse banding derived from margin, plus a station-count rule.
 *
 * Deliberately coarse: a dBm figure means little to anyone but an RF engineer,
 * whereas "critical" on an installer's screen is immediately actionable.
 */
export type SignalQuality = 'unknown' | 'critical' | 'weak' | 'fair' | 'good'

export interface SignalSample {
  /** Device time, ISO 8601. */
  at: string
  seqNum: number
  station: string | null
  /** dBm. Null when the callback omitted it. */
  rssi: number | null
  /** dB. */
  snr: number | null
  /** '04' Test, '05' SNTS, '06' Reading, '07'-'0B' DevConfig, '0C' SensorCfg. */
  msgTypeByte: string | null
}

export interface SignalSummary {
  sampleCount: number
  rssiMin: number | null
  rssiMedian: number | null
  rssiMax: number | null
  /** Median RSSI minus SENSITIVITY_DBM - the headroom that actually matters. */
  marginDb: number | null
  snrMedian: number | null
  /**
   * Every base station that heard this device in the window. One station is a
   * real fragility even at a good margin: nothing takes over when that path
   * fades, which is what separates ~7% delivery from ~91%.
   */
  stations: string[]
  firstAt: string | null
  lastAt: string | null
  quality: SignalQuality
}

export interface DeviceSignal {
  sigfoxId: string
  windowDays: number
  summary: SignalSummary
  /** Newest first. Includes Test messages, so an on-site test appears at once. */
  samples: SignalSample[]
  /** Only when requested with daily=true. Absent from APIs that predate it. */
  daily?: SignalDay[]
}

/** One UTC day of a device's signal, aggregated over every sample that day. */
export interface SignalDay {
  /** YYYY-MM-DD, UTC - the same day key as the missed-messages series. */
  day: string
  sampleCount: number
  rssiMedian: number | null
  rssiMin: number | null
  rssiMax: number | null
  marginDb: number | null
  stationCount: number
  quality: SignalQuality
  /**
   * Which base stations carried that day's messages, busiest first. The
   * callback reports one station per message. Absent from APIs that predate it.
   */
  stations?: SignalDayStation[]
}

export interface SignalDayStation {
  id: string
  count: number
  rssiMedian: number | null
}

export interface FleetSignalRow {
  sigfoxId: string
  summary: SignalSummary
  /**
   * Per-day series over the same window, oldest first, only days with samples
   * and without the per-station breakdown. Present only when asked for, and
   * absent from APIs that predate it.
   */
  daily?: SignalDay[]
}

/** Human labels for the banding. */
export const SIGNAL_QUALITY_LABELS: Record<SignalQuality, string> = {
  unknown: 'No data',
  critical: 'Critical',
  weak: 'Weak',
  fair: 'Fair',
  good: 'Good',
}

/** Tailwind tone per band, matching the palette used elsewhere in the app. */
export const SIGNAL_QUALITY_TONES: Record<SignalQuality, string> = {
  unknown: 'text-gray-500 bg-gray-100',
  critical: 'text-red-700 bg-red-100',
  weak: 'text-amber-700 bg-amber-100',
  fair: 'text-blue-700 bg-blue-100',
  good: 'text-green-700 bg-green-100',
}

/** Message-type byte to a short label, for the sample table. */
export const SIGNAL_MSG_TYPE_LABELS: Record<string, string> = {
  '04': 'Test',
  '05': 'SNTS',
  '06': 'Reading',
  '07': 'DevConfig1',
  '08': 'DevConfig2',
  '09': 'DevConfig3',
  '0A': 'DevConfig4',
  '0B': 'DevConfig5',
  '0C': 'SensorCfg',
}

export function messageTypeLabel(byte: string | null): string {
  if (!byte) return '—'
  return SIGNAL_MSG_TYPE_LABELS[byte.toUpperCase()] ?? `Type:${byte.toUpperCase()}`
}
